import { ArrowLeft } from 'lucide-react';
import {
  type CSSProperties,
  memo,
  type MouseEventHandler,
  type ReactNode,
} from 'react';

import { Button as ShadcnButton } from '@/components/shadcn/ui/button';
import { cn } from '@/lib/cn';

export type PageHeaderProps = {
  title: string;
  desc?: string;
  isBeta?: boolean;
  topRightNode?: ReactNode;
  bottomNode?: ReactNode;
  useBackButton?: {
    onClick: MouseEventHandler<HTMLButtonElement>;
  };
  style?: CSSProperties;
  className?: string;
};

function PageHeaderComponent({
  title,
  desc,
  isBeta = false,
  topRightNode,
  bottomNode,
  useBackButton,
  style,
  className,
}: PageHeaderProps) {
  return (
    <header className={cn('w-full shrink-0', className)}>
      <div className="z-10 flex w-full items-center justify-between overflow-hidden bg-gray-100 px-8 py-5">
        <div className="flex flex-col gap-1">
          <h2 className="m-0 text-base font-semibold leading-tight text-gray-900">
            <span className="flex h-8 items-center gap-2.5">
              <span>{title}</span>
              {isBeta && (
                <span className="inline-flex h-4 w-[38px] items-center justify-center rounded bg-gray-700 text-[10px] font-bold leading-none text-white">
                  BETA
                </span>
              )}
            </span>
          </h2>

          {desc && <p className="min-h-6 text-sm leading-[22px] text-gray-600">{desc}</p>}
        </div>

        {topRightNode}
      </div>

      <div className="px-8 pb-2" style={style}>
        <div className="flex items-center gap-2.5">
          <div className="flex items-center gap-3">
            {useBackButton && (
              <ShadcnButton
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label="Go back"
                onClick={useBackButton.onClick}
              >
                <ArrowLeft className="size-4" />
              </ShadcnButton>
            )}
          </div>
        </div>

        {bottomNode}
      </div>
    </header>
  );
}

export const PageHeader = memo(PageHeaderComponent);

PageHeader.displayName = 'PageHeader';
